import { useState, useEffect } from 'react';
import logger from '@/utils/logger';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Save, Loader2, Plus, X, Image as ImageIcon,
  Package2, FileText, Play, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import Navbar from '@/components/Navbar';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'amigurumi', label: 'Amigurumi' },
  { value: 'clothing', label: 'Clothing' },
  { value: 'accessories', label: 'Accessories' },
  { value: 'home', label: 'Home Decor' },
  { value: 'toys', label: 'Toys' },
  { value: 'bags', label: 'Bags & Purses' },
  { value: 'blankets', label: 'Blankets' },
  { value: 'other', label: 'Other' },
];

const DIFFICULTIES = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
];

const LANGUAGES = [
  { value: 'en', label: 'English', flag: '🇬🇧' },
  { value: 'fi', label: 'Suomi (Finnish)', flag: '🇫🇮' },
];

export default function AdminDesignForm() {
  const { designId } = useParams();
  const navigate = useNavigate();
  const isEditing = !!designId;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'amigurumi',
    difficulty: 'intermediate',
    instructions: '',
    images: [],
    youtube_video_id: '',
    pdf_url: '',
    is_free: false,
    price: '',
    is_published: false,
    is_featured: false,
    tags: '',
    materials_needed: '',
    hook_size: '',
    yarn_weight: '',
    finished_size: '',
    language: 'en',  // Default to English
  });

  const [newImageUrl, setNewImageUrl] = useState('');
  const [newImageCaption, setNewImageCaption] = useState('');

  useEffect(() => {
    if (isEditing) {
      loadDesign();
    } else {
      checkAdmin();
    }
  }, [designId]);

  const checkAdmin = async () => {
    try {
      await axios.get(`${API}/admin/designs`, { withCredentials: true });
      setIsAdmin(true);
    } catch (error) {
      if (error.response?.status === 403) {
        setIsAdmin(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadDesign = async () => {
    try {
      const response = await axios.get(`${API}/admin/designs`, { withCredentials: true });
      const design = response.data.find(d => d.design_id === designId);
      
      if (design) {
        setFormData({
          title: design.title || '',
          description: design.description || '',
          category: design.category || 'amigurumi',
          difficulty: design.difficulty || 'intermediate',
          instructions: design.instructions || '',
          images: design.images || [],
          youtube_video_id: design.youtube_video_id || '',
          pdf_url: design.pdf_url || '',
          is_free: design.is_free || false,
          price: design.price?.toString() || '',
          is_published: design.is_published || false,
          is_featured: design.is_featured || false,
          tags: (design.tags || []).join(', '),
          materials_needed: design.materials_needed || '',
          hook_size: design.hook_size || '',
          yarn_weight: design.yarn_weight || '',
          finished_size: design.finished_size || '',
          language: design.language || 'en',
        });
        setIsAdmin(true);
      } else {
        toast.error('Design not found');
        navigate('/admin/designs');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setIsAdmin(false);
      } else {
        logger.error('Failed to load design:', error);
        toast.error('Failed to load design');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addImage = () => {
    if (!newImageUrl.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, { url: newImageUrl.trim(), caption: newImageCaption.trim() }]
    }));
    setNewImageUrl('');
    setNewImageCaption('');
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const extractYouTubeId = (url) => {
    if (!url) return '';
    // Already an ID
    if (!url.includes('/') && !url.includes('.')) return url;
    
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    if (!formData.is_free && (!formData.price || parseFloat(formData.price) <= 0)) {
      toast.error('Please enter a valid price or mark as free');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        difficulty: formData.difficulty,
        instructions: formData.instructions,
        images: formData.images,
        youtube_video_id: extractYouTubeId(formData.youtube_video_id),
        pdf_url: formData.pdf_url.trim(),
        is_free: formData.is_free,
        price: formData.is_free ? null : parseFloat(formData.price),
        is_published: formData.is_published,
        is_featured: formData.is_featured,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        materials_needed: formData.materials_needed.trim(),
        hook_size: formData.hook_size.trim(),
        yarn_weight: formData.yarn_weight.trim(),
        finished_size: formData.finished_size.trim(),
      };

      if (isEditing) {
        await axios.put(`${API}/admin/designs/${designId}`, payload, { withCredentials: true });
        toast.success('Design updated successfully');
      } else {
        await axios.post(`${API}/admin/designs`, payload, { withCredentials: true });
        toast.success('Design created successfully');
      }

      navigate('/admin/designs');
    } catch (error) {
      logger.error('Failed to save design:', error);
      toast.error(error.response?.data?.detail || 'Failed to save design');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-bold mb-2">Access Denied</h1>
            <p className="text-muted-foreground mb-6">
              You don&apos;t have permission to access the admin area.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="rounded-full">
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/designs')}
          className="mb-6 -ml-2 rounded-full"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Designs
        </Button>

        <div className="mb-8">
          <h1 className="text-4xl font-heading font-extrabold tracking-tight mb-2">
            {isEditing ? 'Edit Design' : 'Create New Design'}
          </h1>
          <p className="text-muted-foreground">
            {isEditing ? 'Update your crochet pattern details' : 'Add a new crochet pattern to your shop'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="rounded-3xl bg-white border border-stone-100 p-8">
            <h2 className="text-xl font-heading font-semibold mb-6 flex items-center">
              <Package2 className="h-5 w-5 mr-2 text-primary" />
              Basic Information
            </h2>
            
            <div className="grid gap-6">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="e.g., Cute Bunny Amigurumi"
                  className="mt-1 rounded-xl"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Describe your pattern..."
                  className="mt-1 rounded-xl min-h-[100px]"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label>Language</Label>
                  <Select value={formData.language} onValueChange={(v) => handleChange('language', v)}>
                    <SelectTrigger className="mt-1 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map(lang => (
                        <SelectItem key={lang.value} value={lang.value}>
                          <span className="flex items-center gap-2">
                            <span>{lang.flag}</span>
                            <span>{lang.label}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Design will appear for users with this language
                  </p>
                </div>

                <div>
                  <Label>Category</Label>
                  <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                    <SelectTrigger className="mt-1 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Difficulty</Label>
                  <Select value={formData.difficulty} onValueChange={(v) => handleChange('difficulty', v)}>
                    <SelectTrigger className="mt-1 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DIFFICULTIES.map(diff => (
                        <SelectItem key={diff.value} value={diff.value}>{diff.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="tags">Tags (comma-separated)</Label>
                <Input
                  id="tags"
                  value={formData.tags}
                  onChange={(e) => handleChange('tags', e.target.value)}
                  placeholder="e.g., bunny, easter, cute, beginner-friendly"
                  className="mt-1 rounded-xl"
                />
              </div>
            </div>
          </div>

          {/* Pattern Details */}
          <div className="rounded-3xl bg-white border border-stone-100 p-8">
            <h2 className="text-xl font-heading font-semibold mb-6 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-primary" />
              Pattern Details
            </h2>
            
            <div className="grid gap-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="hook_size">Hook Size</Label>
                  <Input
                    id="hook_size"
                    value={formData.hook_size}
                    onChange={(e) => handleChange('hook_size', e.target.value)}
                    placeholder="e.g., 3.5mm"
                    className="mt-1 rounded-xl"
                  />
                </div>

                <div>
                  <Label htmlFor="yarn_weight">Yarn Weight</Label>
                  <Input
                    id="yarn_weight"
                    value={formData.yarn_weight}
                    onChange={(e) => handleChange('yarn_weight', e.target.value)}
                    placeholder="e.g., DK / 8 ply"
                    className="mt-1 rounded-xl"
                  />
                </div>

                <div>
                  <Label htmlFor="finished_size">Finished Size</Label>
                  <Input
                    id="finished_size"
                    value={formData.finished_size}
                    onChange={(e) => handleChange('finished_size', e.target.value)}
                    placeholder="e.g., 15cm tall"
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="materials_needed">Materials Needed</Label>
                <Textarea
                  id="materials_needed"
                  value={formData.materials_needed}
                  onChange={(e) => handleChange('materials_needed', e.target.value)}
                  placeholder="List all materials needed..."
                  className="mt-1 rounded-xl min-h-[100px]"
                />
              </div>

              <div>
                <Label htmlFor="instructions">Instructions *</Label>
                <Textarea
                  id="instructions"
                  value={formData.instructions}
                  onChange={(e) => handleChange('instructions', e.target.value)}
                  placeholder="Write the full pattern instructions here..."
                  className="mt-1 rounded-xl min-h-[300px] font-mono text-sm"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  You can use line breaks for formatting. This will be visible to customers after purchase.
                </p>
              </div>
            </div>
          </div>

          {/* Media */}
          <div className="rounded-3xl bg-white border border-stone-100 p-8">
            <h2 className="text-xl font-heading font-semibold mb-6 flex items-center">
              <ImageIcon className="h-5 w-5 mr-2 text-primary" />
              Media
            </h2>
            
            <div className="grid gap-6">
              {/* Images */}
              <div>
                <Label>Images</Label>
                <div className="mt-2 space-y-3">
                  {formData.images.map((img, idx) => (
                    <div key={img.url || idx} className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl">
                      <div className="h-12 w-12 rounded-lg bg-white border overflow-hidden flex-shrink-0">
                        <img src={img.url} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{img.url}</p>
                        {img.caption && <p className="text-xs text-muted-foreground">{img.caption}</p>}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeImage(idx)}
                        className="h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  
                  <div className="flex gap-2">
                    <Input
                      value={newImageUrl}
                      onChange={(e) => setNewImageUrl(e.target.value)}
                      placeholder="Image URL"
                      className="rounded-xl"
                    />
                    <Input
                      value={newImageCaption}
                      onChange={(e) => setNewImageCaption(e.target.value)}
                      placeholder="Caption (optional)"
                      className="rounded-xl w-48"
                    />
                    <Button type="button" onClick={addImage} variant="outline" className="rounded-xl">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* YouTube */}
              <div>
                <Label htmlFor="youtube">YouTube Video</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Play className="h-5 w-5 text-red-500" />
                  <Input
                    id="youtube"
                    value={formData.youtube_video_id}
                    onChange={(e) => handleChange('youtube_video_id', e.target.value)}
                    placeholder="YouTube URL or video ID"
                    className="rounded-xl"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Paste a YouTube URL or just the video ID
                </p>
              </div>

              {/* PDF */}
              <div>
                <Label htmlFor="pdf">PDF Download URL</Label>
                <Input
                  id="pdf"
                  value={formData.pdf_url}
                  onChange={(e) => handleChange('pdf_url', e.target.value)}
                  placeholder="https://..."
                  className="mt-1 rounded-xl"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Link to a downloadable PDF file (Google Drive, Dropbox, etc.)
                </p>
              </div>
            </div>
          </div>

          {/* Pricing & Publishing */}
          <div className="rounded-3xl bg-white border border-stone-100 p-8">
            <h2 className="text-xl font-heading font-semibold mb-6">
              Pricing & Publishing
            </h2>
            
            <div className="grid gap-6">
              <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                <div>
                  <p className="font-medium">Free Pattern</p>
                  <p className="text-sm text-muted-foreground">Make this pattern free for everyone</p>
                </div>
                <Switch
                  checked={formData.is_free}
                  onCheckedChange={(v) => handleChange('is_free', v)}
                />
              </div>

              {!formData.is_free && (
                <div>
                  <Label htmlFor="price">Price (EUR) *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0.50"
                    value={formData.price}
                    onChange={(e) => handleChange('price', e.target.value)}
                    placeholder="e.g., 4.99"
                    className="mt-1 rounded-xl w-40"
                  />
                </div>
              )}

              <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                <div>
                  <p className="font-medium">Published</p>
                  <p className="text-sm text-muted-foreground">Make visible in the shop</p>
                </div>
                <Switch
                  checked={formData.is_published}
                  onCheckedChange={(v) => handleChange('is_published', v)}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                <div>
                  <p className="font-medium">Featured</p>
                  <p className="text-sm text-muted-foreground">Show on the landing page</p>
                </div>
                <Switch
                  checked={formData.is_featured}
                  onCheckedChange={(v) => handleChange('is_featured', v)}
                />
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/designs')}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="rounded-full bg-primary text-primary-foreground shadow-lg"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditing ? 'Update Design' : 'Create Design'}
                </>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
